import React, { useRef, useState, useEffect } from 'react';
import Button from '../../../components/Button';
import FormLightbox from './FormLightbox';
import { useNavigate } from 'react-router-dom';

interface EnquiriesModuleProps {
    className?: string;
    fadeEffect?: boolean;
    retailerFootnote?: string;
    investorFootnote?: string;
}

const EnquiriesModule: React.FC<EnquiriesModuleProps> = ({
    className = '',
    fadeEffect = true,
    retailerFootnote,
    investorFootnote
}) => {
    const moduleRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [activeForm, setActiveForm] = useState<'retailer' | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (fadeEffect) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        setIsVisible(entry.isIntersecting);
                    });
                },
                { threshold: 0.1 }
            );

            if (moduleRef.current) {
                observer.observe(moduleRef.current);
            }

            return () => observer.disconnect();
        }
    }, [fadeEffect]);

    const handleSuccess = (submissionId: string, message: string) => {
        console.log('Form submitted successfully:', submissionId);
    };

    return (
        <>
            <div
                ref={moduleRef}
                className={`w-full ${className} ${fadeEffect ? (isVisible ? 'fade-in' : 'fade-out') : ''}`}
            >
                <div className="module_container">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Retailer Enquiry */}
                        <div className="flex flex-col gap-3">
                            <section className="relative w-full overflow-hidden rounded-3xl bg-orange">
                                <div className="px-6 md:px-10 py-8 md:py-12 flex flex-col gap-6">
                                    <div className="text-center">
                                        <h2 className="text-3xl md:text-4xl font-headline text-white mb-3">
                                            Interested in Distributing?
                                        </h2>
                                        <p className="text-white/90 text-lg">
                                            Stock Kibu headphones in your store and join our mission to make sustainable, repairable headphones accessible to families everywhere.
                                        </p>
                                    </div>
                                    <div className="flex justify-center">
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveForm('retailer');
                                            }}
                                            text="Get in Touch"
                                            variant="solid-white"
                                            showArrow={true}
                                        />
                                    </div>
                                </div>
                            </section>
                            {retailerFootnote && (
                                <p className="text-xs text-gray-500 text-center px-4">
                                    {retailerFootnote}
                                </p>
                            )}
                        </div>

                        {/* Investor Enquiry */}
                        <div className="flex flex-col gap-3">
                            <section className="relative w-full overflow-hidden rounded-3xl bg-green">
                                <div className="px-6 md:px-10 py-8 md:py-12 flex flex-col gap-6">
                                    <div className="text-center">
                                        <h2 className="text-3xl md:text-4xl font-headline text-white mb-3">
                                            Investor enquiries
                                        </h2>
                                        <p className="text-white/90 text-lg">
                                            We're happy to hear from investors and strategic partners who align with our mission.*
                                        </p>
                                    </div>
                                    <div className="flex justify-center">
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/investor');
                                            }}
                                            text="Learn More"
                                            variant="solid-white"
                                            showArrow={true}
                                        />
                                    </div>
                                </div>
                            </section>
                            {investorFootnote && (
                                <p className="text-xs text-gray-500 text-center px-4">
                                    *{investorFootnote}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Retailer Form Lightbox */}
            {activeForm === 'retailer' && (
                <FormLightbox
                    isOpen={activeForm === 'retailer'}
                    onClose={() => setActiveForm(null)}
                    formId="distributor_enquiry"
                    formTitle="Become a Kibu Retailer"
                    formDescription="Fill out the form below and our partnerships team will get in touch with you soon."
                    onSuccess={handleSuccess}
                    metadata={{
                        source: 'home_enquiries_module'
                    }}
                />
            )}

        </>
    );
};

export default EnquiriesModule;
